import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import { ButtonView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { getScreenTop } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver, { user } from 'src/StateObserver';
import { createEmitter } from 'src/lib/Emitter';
import sounds from 'src/lib/sounds';
import { Locale } from 'src/replicant/ruleset/locale';
import features from 'src/replicant/ruleset/features';

import { AssetGroup } from '@play-co/timestep-core';

const linkBounds = {
  tos: {
    en: {
      x: 410,
      y: 27,
      width: 165,
    },
    ja: {
      x: 351,
      y: 27,
      width: 80,
    },
  },
  pn: {
    en: {
      x: 247,
      y: 56,
      width: 139,
    },
    ja: {
      x: 100,
      y: 56,
      width: 159,
    },
  },
};

export default class PopupAcceptToS extends PopupBasic {
  private bottomContainer: View;
  private termsOfServiceButton: ButtonView;
  private privacyNoticeButton: ButtonView;
  private tosText: LangBitmapFontTextView;
  private termsOfServiceButtonUnderline: View;
  private privacyNoticeButtonUnderline: View;

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: 443,
      height: 155,
      skipTitle: true,
      boxOpts: {
        image: 'assets/ui/conversation/thoughtbubble.png',
        scaleMethod: 'contain',
      },
      tracking: { feature: 'tutorial', $subFeature: 'popup-accept-tos' },
    });

    this.buttonClose.removeFromSuperview();

    this.bg.updateOpts({
      backgroundColor: undefined,
      image: 'assets/ui/shared/frames/popup_overlay.png',
      scaleMethod: 'contain',
      verticalAlign: 'bottom',
    });

    this.message.updateOpts({
      color: '#91808C',
      size: 30,
      width: this.box.style.width - 60,
      height: this.box.style.height - 30,
      x: 30,
      y: 10,
      wordWrap: false,
    });

    const width = uiConfig.width - 80;
    this.bottomContainer = this.box.addSubview(
      new View({
        height: 275,
        width,
        x: this.box.style.width / 2,
        centerOnOrigin: true,
      }),
    );

    const copyKey = features(user()).isGuestMode
      ? 'tutorial.acceptToS.copyGuest'
      : 'tutorial.acceptToS.copy';
    this.tosText = this.bottomContainer.addSubview(
      new LangBitmapFontTextView({
        font: 'Legal',
        size: 20,
        align: 'center',
        width,
        localeText: () => i18n(copyKey),
      }),
    );

    const linkButtonHeight = 22;
    const underlineUI = {
      y: 20,
      width: 300,
      height: 2,
      backgroundColor: '#CCC',
    };
    this.termsOfServiceButton = new ButtonView({
      superview: this.bottomContainer,
      clip: true,
      height: linkButtonHeight,
      onClick: () =>
        openPopupPromise('popupTermsPolicyDetails', { mode: 'tos' }),
    });
    this.termsOfServiceButtonUnderline = this.termsOfServiceButton.addSubview(
      new View(underlineUI),
    );
    this.privacyNoticeButton = new ButtonView({
      superview: this.bottomContainer,
      clip: true,
      height: linkButtonHeight,
      onClick: () =>
        openPopupPromise('popupTermsPolicyDetails', { mode: 'pp' }),
    });
    this.privacyNoticeButtonUnderline = this.privacyNoticeButton.addSubview(
      new View(underlineUI),
    );

    this.bottomContainer.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: width / 2,
        y: 195,
        width: 240,
        height: 90,
        centerOnOrigin: true,
        localeText: () => i18n('tutorial.acceptToS.accept'),
        onClick: async () => {
          sounds.playSound('ok');
          opts.close();
        },
        tracking: {
          feature: 'tutorial',
          $subFeature: 'popup-accept-tos-open',
        },
      }),
    );
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/conversation/thoughtbubble.png',
      'assets/ui/shared/frames/popup_overlay.png',
    ]);
  }

  public init(opts: { showTos: boolean }) {
    super.init({
      message: i18n('tutorial.acceptToS.message'),
    });

    const { screenSize } = StateObserver.getState().ui;
    const y = uiConfig.height * 0.4;
    this.box.updateOpts({ y });
    this.bottomContainer.updateOpts({
      y: getScreenTop() + screenSize.height - 250 - y,
      offsetY: 0,
    });

    if (opts.showTos) {
      this.tosText.show();
      this.privacyNoticeButtonUnderline.show();
      this.termsOfServiceButtonUnderline.show();
    } else {
      this.privacyNoticeButtonUnderline.hide();
      this.termsOfServiceButtonUnderline.hide();
      this.tosText.hide();
    }

    createEmitter(this.root, ({ ui }) => ui.locale as Locale).addListener(
      (locale) => {
        this.privacyNoticeButton.updateOpts(linkBounds.pn[locale]);
        this.termsOfServiceButton.updateOpts(linkBounds.tos[locale]);
      },
    );
  }
}
